import { buildLuceneFieldClause, transformLuceneClauses, } from '../lucene-transformer.util';
import { GRPC_ERROR_STATUS_NAMES } from './grpc-status-names';
import { JAEGER_PROCESS_TAG_PREFIX, JAEGER_TAG_PREFIX, OTEL_JAEGER_FIELD_REGISTRY } from './otel-jaeger-field-registry';
import { getJaegerSearchFieldTargets } from './spans-search-target-builder.util';
const SPAN_TYPE_CLAUSE = 'type: jaegerSpan';
const OTEL_STATUS_CODE_FIELD = 'otel.status_code';
const JAEGER_DURATION_FIELD = 'duration';
const SPAN_DURATION_MIN_FIELD = 'span.duration.min';
const SPAN_DURATION_MAX_FIELD = 'span.duration.max';
const SPAN_DURATION_MAX_EXCLUSIVE_FIELD = 'span.duration.maxExclusive';
const JAEGER_OTEL_STATUS_CODE_FIELD = 'JaegerTag.otel@status_code';
const JAEGER_HTTP_RESPONSE_STATUS_CODE_FIELD = 'JaegerTag.http@response@status_code';
const JAEGER_ERROR_FIELD = 'JaegerTag.error';
const JAEGER_HTTP_STATUS_CODE_FIELD = 'JaegerTag.http@status_code';
const JAEGER_GRPC_STATUS_CODE_FIELD = 'JaegerTag.rpc@grpc@status_code';
const JAEGER_RPC_RESPONSE_STATUS_CODE_FIELD = 'JaegerTag.rpc@response@status_code';
const JAEGER_FIELD_NAMES = new Set(Object.values(OTEL_JAEGER_FIELD_REGISTRY).map(mapping => mapping.jaeger));
const buildDualPathJaegerTagPredicate = (jaegerTagField, predicate) => {
    const processTagField = `${JAEGER_PROCESS_TAG_PREFIX}${jaegerTagField.slice(JAEGER_TAG_PREFIX.length)}`;
    return `(${jaegerTagField}${predicate} OR ${processTagField}${predicate})`;
};
const OK_STATUS_CLAUSE = `(${buildDualPathJaegerTagPredicate(JAEGER_OTEL_STATUS_CODE_FIELD, ': "OK"')} OR ${buildDualPathJaegerTagPredicate(JAEGER_HTTP_RESPONSE_STATUS_CODE_FIELD, ':[200 TO 399]')} OR ${buildDualPathJaegerTagPredicate(JAEGER_HTTP_STATUS_CODE_FIELD, ':[200 TO 399]')} OR ${buildDualPathJaegerTagPredicate(JAEGER_GRPC_STATUS_CODE_FIELD, ': 0')} OR ${buildDualPathJaegerTagPredicate(JAEGER_RPC_RESPONSE_STATUS_CODE_FIELD, ': "OK"')})`;
const ERROR_STATUS_CLAUSE = `(${buildDualPathJaegerTagPredicate(JAEGER_OTEL_STATUS_CODE_FIELD, ': "ERROR"')} OR ${buildDualPathJaegerTagPredicate(JAEGER_ERROR_FIELD, ': true')} OR ${buildDualPathJaegerTagPredicate(JAEGER_HTTP_RESPONSE_STATUS_CODE_FIELD, ':[400 TO *]')} OR ${buildDualPathJaegerTagPredicate(JAEGER_HTTP_STATUS_CODE_FIELD, ':[400 TO *]')} OR ${buildDualPathJaegerTagPredicate(JAEGER_GRPC_STATUS_CODE_FIELD, ':[1 TO *]')} OR ${buildDualPathJaegerTagPredicate(JAEGER_RPC_RESPONSE_STATUS_CODE_FIELD, `:(${GRPC_ERROR_STATUS_NAMES.map(name => `"${name}"`).join(' OR ')})`)})`;
const UNSET_STATUS_CLAUSE = `(${buildDualPathJaegerTagPredicate(JAEGER_OTEL_STATUS_CODE_FIELD, ': "UNSET"')} OR NOT (${OK_STATUS_CLAUSE} OR ${ERROR_STATUS_CLAUSE}))`;
export const buildOtelStatusCodeLuceneClause = (value, op) => {
    let clause;
    if (value === 'OK') {
        clause = OK_STATUS_CLAUSE;
    }
    else if (value === 'ERROR') {
        clause = ERROR_STATUS_CLAUSE;
    }
    else if (value === 'UNSET') {
        clause = UNSET_STATUS_CLAUSE;
    }
    else {
        return undefined;
    }
    return op === 'is_not' ? `NOT ${clause}` : clause;
};
const rewriteOtelFieldInLucene = (field) => {
    if (JAEGER_FIELD_NAMES.has(field) ||
        field.startsWith(JAEGER_TAG_PREFIX) ||
        field.startsWith(JAEGER_PROCESS_TAG_PREFIX)) {
        return false;
    }
    return getJaegerSearchFieldTargets(field);
};
const unquoteLuceneValue = (value) => value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1).replace(/\\(.)/g, '$1') : value;
/**
 * Span status is derived rather than stored, so a typed `otel.status_code` clause gets the same
 * expansion the filter panel builds. Any surrounding `NOT` stays in the query around it.
 */
const rewriteOtelClauseInLucene = (clause) => {
    if (clause.field === OTEL_STATUS_CODE_FIELD) {
        const statusClause = buildOtelStatusCodeLuceneClause(unquoteLuceneValue(clause.value), 'is');
        if (statusClause) {
            return statusClause;
        }
    }
    const transformed = rewriteOtelFieldInLucene(clause.field);
    if (transformed === false) {
        return false;
    }
    return buildLuceneFieldClause(typeof transformed === 'string' ? [transformed] : transformed, clause);
};
export const translateOtelFieldsInLuceneQuery = (query) => transformLuceneClauses(query, rewriteOtelClauseInLucene);
const buildSpanDurationRangeClause = (field, value, op) => {
    const rangeByField = {
        [SPAN_DURATION_MIN_FIELD]: val => `${JAEGER_DURATION_FIELD}:[${val} TO *]`,
        [SPAN_DURATION_MAX_FIELD]: val => `${JAEGER_DURATION_FIELD}:[* TO ${val}]`,
        [SPAN_DURATION_MAX_EXCLUSIVE_FIELD]: val => `${JAEGER_DURATION_FIELD}:[* TO ${val}}`,
    };
    const buildRange = rangeByField[field];
    if (!buildRange || typeof value !== 'number' || Number.isNaN(value)) {
        return undefined;
    }
    const clause = buildRange(value);
    return op === 'is_not' ? `NOT ${clause}` : clause;
};
const quoteLuceneValue = (value) => {
    const str = String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `"${str}"`;
};
const buildFieldValueClause = (jaegerFields, value) => {
    const quotedValue = quoteLuceneValue(value);
    if (jaegerFields.length === 1) {
        return `${jaegerFields[0]}: ${quotedValue}`;
    }
    return `(${jaegerFields.map(field => `${field}: ${quotedValue}`).join(' OR ')})`;
};
export const buildSpanLuceneQuery = (spanFilters, luceneQuery) => {
    const clauses = [SPAN_TYPE_CLAUSE];
    const trimmedLuceneQuery = luceneQuery?.trim();
    if (trimmedLuceneQuery && trimmedLuceneQuery !== '*') {
        clauses.push(`(${translateOtelFieldsInLuceneQuery(trimmedLuceneQuery)})`);
    }
    spanFilters.forEach(({ field, value, op }) => {
        if (field === OTEL_STATUS_CODE_FIELD) {
            const statusClause = buildOtelStatusCodeLuceneClause(value, op);
            if (statusClause) {
                clauses.push(statusClause);
                return;
            }
        }
        const durationClause = buildSpanDurationRangeClause(field, value, op);
        if (durationClause) {
            clauses.push(durationClause);
            return;
        }
        const clause = buildFieldValueClause(getJaegerSearchFieldTargets(field), value);
        clauses.push(op === 'is_not' ? `NOT ${clause}` : clause);
    });
    return clauses.join(' AND ');
};
//# sourceMappingURL=build-span-lucene-query.util.js.map